import React, { useState, useEffect } from "react";
import {
    Box,
    Avatar,
    Typography,
    Paper,
    CircularProgress,
    Button,
    Divider,
    Grid,
    Chip,
    Tooltip,
    Card,
    CardContent,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PlaceIcon from "@mui/icons-material/Place";
import PaymentIcon from "@mui/icons-material/Payment";
import EventIcon from "@mui/icons-material/Event";
import { decryptId } from "../helper/idCodec.js";
import { useParams, useNavigate } from "react-router-dom";
import { getData, API_ENDPOINTS } from "../../services/apiService.js";
import "./Users.css";

function UserDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const userId = id ? decryptId(id) : "";

    const [customerData, setCustomerData] = useState(null);
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pkgLoading, setPkgLoading] = useState(true);

    // Fetch customer details
    useEffect(() => {
        if (!userId) return;
        const fetchCustomerData = async () => {
            try {
                const res = await getData(API_ENDPOINTS.findCustomerById, { customer_id: userId });
                if (res.statusCode === 200 && res.result?.customer_details) {
                    setCustomerData(res.result.customer_details);
                }
            } catch (error) {
                console.error("Error fetching customer details:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchCustomerData();
    }, [userId]);

    // Fetch customer packages
    useEffect(() => {
        if (!userId) return;
        const fetchPackages = async () => {
            try {
                const res = await getData(API_ENDPOINTS.fetchPackagesByCustomer, {
                    customer_id: userId,
                    page: 1,
                    limit: 100,
                });
                if (res.statusCode === 200 && res.result?.packages) {
                    setPackages(res.result.packages);
                }
            } catch (err) {
                console.error("Error fetching packages:", err);
            } finally {
                setPkgLoading(false);
            }
        };
        fetchPackages();
    }, [userId]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
                <CircularProgress />
            </Box>
        );
    }

    if (!customerData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
                <Typography color="error">Failed to load customer details.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            {/* Back Button */}
            <Button
                variant="outlined"
                startIcon={<ArrowBackIosNewIcon />}
                onClick={() => navigate("/app/users")}
                sx={{
                    textTransform: "none",
                    borderRadius: "30px",
                    px: 2.5,
                    py: 1,
                    fontWeight: 500,
                    color: "#333",
                    borderColor: "#ccc",
                    backgroundColor: "#fff",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                    "&:hover": {
                        backgroundColor: "#f5f5f5",
                        boxShadow: "0 3px 8px rgba(0,0,0,0.12)",
                    },
                }}
            >
                Back to Users
            </Button>

            {/* Customer Card */}
            <Paper
                elevation={4}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 3,
                    borderRadius: "16px",
                    background: "#fafafa",
                    boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                    mt: 2,
                }}
            >
                <Avatar
                    src={customerData.pic || "/smart-vectors/default-user.png"}
                    alt={`${customerData.first_name || ""} ${customerData.last_name || ""}`}
                    sx={{ width: 120, height: 120, border: "4px solid #eee", mr: 3 }}
                />
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={600}>
                        {customerData.first_name} {customerData.last_name}
                    </Typography>
                    <Typography sx={{ color: "#333" }}>📱 {customerData.mobile_num || "N/A"}</Typography>
                    <Typography sx={{ color: "#333" }}>📧 {customerData.email || "N/A"}</Typography>
                    <Typography sx={{ color: "#333" }}>
                        🏙️ {customerData.city || "N/A"}, {customerData.state || "N/A"}
                    </Typography>
                    <Typography sx={{ color: "#666" }}>
                        📅 Joined: {new Date(customerData.createdAt).toLocaleDateString()}
                    </Typography>
                </Box>
            </Paper>

            {/* Packages Section */}
            <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 1 }}>
                Packages
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {pkgLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="30vh">
                    <CircularProgress />
                </Box>
            ) : packages.length === 0 ? (
                <Typography sx={{ textAlign: "center", mt: 4, color: "gray" }}>
                    No packages found for this user.
                </Typography>
            ) : (
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        justifyContent: "space-between",
                    }}
                >
                    {packages.map((pkg) => (
                        <Box
                            key={pkg.mapping_id}
                            sx={{
                                flex: "0 0 calc(50% - 8px)", // two columns per row
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <Card
                                elevation={3}
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    minHeight: 260,
                                    borderRadius: "16px",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                                    "&:hover": {
                                        transform: "translateY(-5px)",
                                        boxShadow: "0 6px 15px rgba(0,0,0,0.15)",
                                    },
                                }}
                            >
                                <CardContent>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="h6" fontWeight={600}>
                                            {pkg.package_name}
                                        </Typography>
                                        <Chip
                                            label={pkg.status}
                                            color={
                                                pkg.status === "Accepted"
                                                    ? "success"
                                                    : pkg.status === "Requested"
                                                        ? "warning"
                                                        : "default"
                                            }
                                            size="small"
                                        />
                                    </Box>

                                    <Typography variant="body2" color="text.secondary">
                                        Ref ID: {pkg.reference_id}
                                    </Typography>

                                    <Divider sx={{ my: 1 }} />

                                    {/* Pickup and Drop */}
                                    <Box sx={{ mb: 1 }}>
                                        <Tooltip title="Pickup Point">
                                            <Box display="flex" alignItems="center" mb={0.5}>
                                                <PlaceIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                                                <Typography variant="body2">
                                                    From: {pkg.pickup_point?.point || "N/A"} ({pkg.pickup_point?.city || "N/A"})
                                                </Typography>
                                            </Box>
                                        </Tooltip>

                                        <Tooltip title="Drop Point">
                                            <Box display="flex" alignItems="center">
                                                <PlaceIcon fontSize="small" color="error" sx={{ mr: 1 }} />
                                                <Typography variant="body2">
                                                    To: {pkg.drop_point?.point || "N/A"} ({pkg.drop_point?.city || "N/A"})
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    </Box>

                                    {/* Truck Info */}
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <LocalShippingIcon fontSize="small" sx={{ mr: 1 }} color="action" />
                                        <Typography variant="body2">
                                            Truck: {pkg.truck?.truck_name || "N/A"} ({pkg.truck?.truck_no})
                                        </Typography>
                                    </Box>

                                    {/* Payment Info */}
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <PaymentIcon fontSize="small" sx={{ mr: 1 }} color="action" />
                                        <Typography variant="body2">
                                            ₹{pkg.payment?.amount || "0.00"} ({pkg.payment?.status || "N/A"})
                                        </Typography>
                                    </Box>

                                    {/* Dates */}
                                    <Box display="flex" alignItems="center">
                                        <EventIcon fontSize="small" sx={{ mr: 1 }} color="action" />
                                        <Typography variant="body2">
                                            Pickup:{" "}
                                            {pkg.pickup_date
                                                ? new Date(pkg.pickup_date).toLocaleDateString()
                                                : "N/A"}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}

export default UserDetails;
